/**
 * SecurityTestingManager - Super Admin tools
 * 1. View As Company — test supply chain data isolation by switching company context
 * 2. Security Testing — test different user roles/groups without logging out
 * Both use session storage via SecurityTestingController endpoints.
 */

interface SecurityGroup {
    id: string;
    name: string;
}

interface SecurityRole {
    value: string;
    display: string;
}

interface CompanyItem {
    id: number;
    name: string;
}

interface TestingState {
    active: boolean;
    groupId?: string;
    role?: string;
    viewAsCompanyId?: number;
    viewAsCompanyName?: string;
}

interface ApiResult {
    success: boolean;
}

type StatusType = 'success' | 'error' | 'warning' | 'info';

export class SecurityTestingManager {
    public initialize(): void {
        // Check for the admin tools panel elements in the dashboard dropdown
        if (!document.getElementById('testGroupSelect')) return;

        this.loadCompanies();
        this.loadGroups();
        this.loadRoles();
        this.loadCurrentState();
        this.bindEvents();
    }

    // ─── Company "View As" ───────────────────────────

    private async loadCompanies(): Promise<void> {
        try {
            const response: Response = await fetch('/api/SecurityTesting/GetAvailableCompanies', { credentials: 'same-origin' });
            if (!response.ok) return;
            const companies: CompanyItem[] = await response.json();
            const select: HTMLSelectElement | null = document.getElementById('viewAsCompanySelect') as HTMLSelectElement | null;
            if (!select) return;

            select.innerHTML = '<option value="">My Company (default)</option>';
            companies.forEach((company: CompanyItem) => {
                const option: HTMLOptionElement = document.createElement('option');
                option.value = company.id.toString();
                option.textContent = company.name;
                option.dataset.name = company.name;
                select.appendChild(option);
            });
        } catch (error: unknown) {
            console.error('Error loading companies:', error);
        }
    }

    private async activateCompanyView(): Promise<void> {
        const select: HTMLSelectElement | null = document.getElementById('viewAsCompanySelect') as HTMLSelectElement | null;
        if (!select || !select.value) {
            this.showCompanyStatus('Please select a company', 'warning');
            return;
        }

        const companyId: string = select.value;
        const companyName: string = select.options[select.selectedIndex]?.text || '';

        try {
            const response: Response = await fetch('/api/SecurityTesting/SetViewAsCompany', {
                method: 'POST',
                headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
                credentials: 'same-origin',
                body: `companyId=${encodeURIComponent(companyId)}&companyName=${encodeURIComponent(companyName)}`
            });

            if (response.ok) {
                const result: ApiResult = await response.json();
                if (result.success) {
                    this.showCompanyStatus(`Viewing as: ${companyName}`, 'success');
                    this.updateDashboardButton(true, companyName);
                    setTimeout(() => { window.location.reload(); }, 800);
                } else {
                    this.showCompanyStatus('Failed to set company view', 'error');
                }
            }
        } catch (error: unknown) {
            console.error('Error setting company view:', error);
            this.showCompanyStatus('Error setting company view', 'error');
        }
    }

    private async clearCompanyView(): Promise<void> {
        try {
            const response: Response = await fetch('/api/SecurityTesting/ClearViewAsCompany', {
                method: 'POST',
                credentials: 'same-origin'
            });

            if (response.ok) {
                const result: ApiResult = await response.json();
                if (result.success) {
                    this.showCompanyStatus('Company view cleared', 'success');
                    const select: HTMLSelectElement | null = document.getElementById('viewAsCompanySelect') as HTMLSelectElement | null;
                    if (select) select.value = '';
                    this.updateCompanyButton();
                    this.updateDashboardButton(false);
                    setTimeout(() => { window.location.reload(); }, 800);
                }
            }
        } catch (error: unknown) {
            console.error('Error clearing company view:', error);
            this.showCompanyStatus('Error clearing company view', 'error');
        }
    }

    private updateCompanyButton(): void {
        const select: HTMLSelectElement | null = document.getElementById('viewAsCompanySelect') as HTMLSelectElement | null;
        const btn: HTMLButtonElement | null = document.getElementById('activateCompanyBtn') as HTMLButtonElement | null;
        if (btn) btn.disabled = !select?.value;
    }

    private showCompanyStatus(message: string, type: StatusType = 'info'): void {
        const statusDiv: HTMLElement | null = document.getElementById('companyStatus');
        if (!statusDiv) return;

        const colors: Record<StatusType, string> = {
            success: '#28a745', error: '#dc3545', warning: '#ffc107', info: '#17a2b8'
        };

        statusDiv.innerHTML = `<span style="color: ${colors[type]}; font-weight: bold;">${message}</span>`;
        setTimeout(() => { statusDiv.innerHTML = ''; }, 3000);
    }

    // ─── Group/Role Testing ──────────────────────────

    private async loadGroups(): Promise<void> {
        try {
            const response: Response = await fetch('/api/SecurityTesting/GetAvailableGroups', { credentials: 'same-origin' });
            if (!response.ok) return;
            const groups: SecurityGroup[] = await response.json();
            const select: HTMLSelectElement | null = document.getElementById('testGroupSelect') as HTMLSelectElement | null;
            if (!select) return;

            select.innerHTML = '<option value="">Select Group...</option>';
            groups.forEach((group: SecurityGroup) => {
                const option: HTMLOptionElement = document.createElement('option');
                option.value = group.id;
                option.textContent = group.name;
                select.appendChild(option);
            });
        } catch (error: unknown) {
            console.error('Error loading groups:', error);
        }
    }

    private async loadRoles(): Promise<void> {
        try {
            const response: Response = await fetch('/api/SecurityTesting/GetAvailableRoles', { credentials: 'same-origin' });
            if (!response.ok) return;
            const roles: SecurityRole[] = await response.json();
            const select: HTMLSelectElement | null = document.getElementById('testRoleSelect') as HTMLSelectElement | null;
            if (!select) return;

            select.innerHTML = '<option value="">Select Role...</option>';
            roles.forEach((role: SecurityRole) => {
                const option: HTMLOptionElement = document.createElement('option');
                option.value = role.value;
                option.textContent = role.display;
                select.appendChild(option);
            });
        } catch (error: unknown) {
            console.error('Error loading roles:', error);
        }
    }

    // ─── Shared state ────────────────────────────────

    private async loadCurrentState(): Promise<void> {
        try {
            const response: Response = await fetch('/api/SecurityTesting/GetCurrentTestingState', { credentials: 'same-origin' });
            if (!response.ok) return;
            const state: TestingState = await response.json();

            // Restore company view state
            if (state.viewAsCompanyId) {
                const companySelect: HTMLSelectElement | null = document.getElementById('viewAsCompanySelect') as HTMLSelectElement | null;
                if (companySelect) companySelect.value = state.viewAsCompanyId.toString();
                this.updateCompanyButton();
                this.updateDashboardButton(true, state.viewAsCompanyName || `Company #${state.viewAsCompanyId}`);
            }

            // Restore group/role testing state
            if (state.active) {
                const groupSelect: HTMLSelectElement | null = document.getElementById('testGroupSelect') as HTMLSelectElement | null;
                const roleSelect: HTMLSelectElement | null = document.getElementById('testRoleSelect') as HTMLSelectElement | null;
                if (state.groupId && groupSelect) groupSelect.value = state.groupId;
                if (state.role && roleSelect) roleSelect.value = state.role;
                this.addPageTestingIndicator(this.getSelectedGroupName(), state.role || '');
            }
            this.updateUI(state.active);
        } catch (error: unknown) {
            console.error('Error loading current state:', error);
        }
    }

    private bindEvents(): void {
        // Company view events
        document.getElementById('activateCompanyBtn')?.addEventListener('click', () => this.activateCompanyView());
        document.getElementById('clearCompanyBtn')?.addEventListener('click', () => this.clearCompanyView());
        document.getElementById('viewAsCompanySelect')?.addEventListener('change', () => this.updateCompanyButton());

        // Group/role testing events
        document.getElementById('activateTestingBtn')?.addEventListener('click', () => this.activateTesting());
        document.getElementById('clearTestingBtn')?.addEventListener('click', () => this.clearTesting());
        document.getElementById('testGroupSelect')?.addEventListener('change', () => this.updateActivateButton());
        document.getElementById('testRoleSelect')?.addEventListener('change', () => this.updateActivateButton());
    }

    private updateActivateButton(): void {
        const groupId: string = (document.getElementById('testGroupSelect') as HTMLSelectElement | null)?.value || '';
        const role: string = (document.getElementById('testRoleSelect') as HTMLSelectElement | null)?.value || '';
        const btn: HTMLButtonElement | null = document.getElementById('activateTestingBtn') as HTMLButtonElement | null;
        if (btn) btn.disabled = !groupId || !role;
    }

    private async activateTesting(): Promise<void> {
        const groupId: string = (document.getElementById('testGroupSelect') as HTMLSelectElement | null)?.value || '';
        const role: string = (document.getElementById('testRoleSelect') as HTMLSelectElement | null)?.value || '';

        if (!groupId || !role) {
            this.showStatus('Please select both a group and role', 'warning');
            return;
        }

        try {
            const response: Response = await fetch('/api/SecurityTesting/SetTestingMode', {
                method: 'POST',
                headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
                credentials: 'same-origin',
                body: `groupId=${encodeURIComponent(groupId)}&role=${encodeURIComponent(role)}`
            });

            if (response.ok) {
                const result: ApiResult = await response.json();
                if (result.success) {
                    const groupName: string = this.getSelectedGroupName();
                    this.showStatus(`Testing as: ${groupName} / ${role}`, 'success');
                    this.updateUI(true);
                    this.addPageTestingIndicator(groupName, role);
                    setTimeout(() => { window.location.reload(); }, 1000);
                } else {
                    this.showStatus('Failed to activate testing mode', 'error');
                }
            }
        } catch (error: unknown) {
            console.error('Error activating testing:', error);
            this.showStatus('Error activating testing mode', 'error');
        }
    }

    private async clearTesting(): Promise<void> {
        try {
            const response: Response = await fetch('/api/SecurityTesting/ClearTestingMode', { method: 'POST', credentials: 'same-origin' });
            if (response.ok) {
                const result: ApiResult = await response.json();
                if (result.success) {
                    this.showStatus('Testing mode cleared', 'success');
                    this.updateUI(false);
                    const groupSelect: HTMLSelectElement | null = document.getElementById('testGroupSelect') as HTMLSelectElement | null;
                    const roleSelect: HTMLSelectElement | null = document.getElementById('testRoleSelect') as HTMLSelectElement | null;
                    if (groupSelect) groupSelect.value = '';
                    if (roleSelect) roleSelect.value = '';
                    this.removePageTestingIndicator();
                    setTimeout(() => { window.location.reload(); }, 1000);
                }
            }
        } catch (error: unknown) {
            console.error('Error clearing testing:', error);
            this.showStatus('Error clearing testing mode', 'error');
        }
    }

    // ─── UI helpers ──────────────────────────────────

    private getSelectedGroupName(): string {
        const select: HTMLSelectElement | null = document.getElementById('testGroupSelect') as HTMLSelectElement | null;
        return select?.options[select.selectedIndex]?.text || 'Unknown Group';
    }

    private updateUI(isActive = false): void {
        this.updateActivateButton();

        // Only colour dashboards button for group/role testing if no company view is active
        const companySelect: HTMLSelectElement | null = document.getElementById('viewAsCompanySelect') as HTMLSelectElement | null;
        if (companySelect?.value) return; // company view takes precedence for button styling

        const dashBtn: HTMLElement | null = document.getElementById('dashboardsMenuButton');
        if (!dashBtn) return;

        if (isActive) {
            dashBtn.style.background = 'linear-gradient(45deg, #ff6b6b, #ffa500)';
            dashBtn.style.color = 'white';
        } else {
            dashBtn.style.background = '';
            dashBtn.style.color = '';
        }
    }

    private updateDashboardButton(companyActive: boolean, companyName?: string): void {
        const dashBtn: HTMLElement | null = document.getElementById('dashboardsMenuButton');
        if (!dashBtn) return;

        if (companyActive) {
            dashBtn.style.background = 'linear-gradient(45deg, #e65100, #bf360c)';
            dashBtn.style.color = 'white';
            dashBtn.title = `Viewing as: ${companyName}`;
        } else {
            dashBtn.style.background = '';
            dashBtn.style.color = '';
            dashBtn.title = '';
        }
    }

    private addPageTestingIndicator(groupName: string, role: string): void {
        const dashBtn: HTMLElement | null = document.getElementById('dashboardsMenuButton');
        if (dashBtn) {
            dashBtn.style.background = 'linear-gradient(45deg, #ff6b6b, #ffa500)';
            dashBtn.style.color = 'white';
            dashBtn.title = `Testing as: ${groupName} / ${role}`;
        }
    }

    private removePageTestingIndicator(): void {
        const dashBtn: HTMLElement | null = document.getElementById('dashboardsMenuButton');
        if (dashBtn) {
            dashBtn.style.background = '';
            dashBtn.style.color = '';
            dashBtn.title = '';
        }
    }

    private showStatus(message: string, type: StatusType = 'info'): void {
        const statusDiv: HTMLElement | null = document.getElementById('testingStatus');
        if (!statusDiv) return;

        const colors: Record<StatusType, string> = {
            success: '#28a745', error: '#dc3545', warning: '#ffc107', info: '#17a2b8'
        };

        statusDiv.innerHTML = `<span style="color: ${colors[type]}; font-weight: bold;">${message}</span>`;
        setTimeout(() => { statusDiv.innerHTML = ''; }, 3000);
    }
}

// Auto-initialize
document.addEventListener('DOMContentLoaded', () => {
    const manager: SecurityTestingManager = new SecurityTestingManager();
    manager.initialize();
});

export default SecurityTestingManager;
